import { FC, useEffect, useState } from "react";
import { OrderDisplay } from "./OrdersTable/OrdersTableDef";
import { ActionButton, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import React from "react";
import { createOrdersClient } from "../lib/api";
import { useAsync } from "../hooks/useAsync";
import { useLoadLocalOrderLines } from "../hooks/useLoadOrderLies";
import { useAppState } from "../context/AppContext";
import { useWriteToOrderLines } from "../hooks/useWriteToOrderLines";
import { OrderLineModel } from "../lib/models/OrderLine";

type OrderTransfererProps = {
  orders: OrderDisplay[];
  onError: (error: string) => void;
};
export const OrderTransferer: FC<OrderTransfererProps> = ({ orders, onError }) => {
  const { execute: sendToBusinessCentral, error, isLoading } = useAsync();
  const [syncedToFile, setSyncedToFile] = useState(true);
  const { reload } = useLoadLocalOrderLines({ noInitialLoad: true });
  const { state } = useAppState();
  const { write } = useWriteToOrderLines();

  useEffect(() => {
    onError(error?.message || "");
  }, [error]);

  const transfer = () => {
    const deliveryIds = new Set(orders.map((o) => o.postId));
    // Send only orders that are not already sent and do not have an order number
    const toSend = state.orderLines.filter((ol) => deliveryIds.has(ol.deliveryId) && !ol.orderNo);
    setSyncedToFile(false);
    sendToBusinessCentral(
      {
        onComplete: async (data) => {
          if (!data) return;
          const newLines = state.orderLines.map((ol) => {
            const newLine = new OrderLineModel(ol.toExcelRow(), ol.originalIndex);
            if (deliveryIds.has(newLine.deliveryId)) {
              const d = data.find((d) => d.LineNo == ol.lineNo && d.DeliveryID == ol.deliveryId);
              newLine.sentBusinessCentral = true;
              if (!newLine.sentTime) {
                newLine.sentTime = new Date().toISOString();
              }
              if (!newLine.sentByUser || newLine.sentByUser == "unknown") {
                newLine.sentByUser = d?.TransferredUserID || "unknown";
              }
              if (d) {
                newLine.orderNo = d.OrderNo || "";
                newLine.orderUrl = d?.OrderHyperlink || "";
                newLine.orderCreatedTime = d?.OrderCreatedDT || "";
                newLine.businessCentralStatusMessage = d?.StatusMsgERP || "";
                newLine.businessCentralStatusCode = d?.StatusCodeERP || "";
              }
            }
            return newLine;
          });
          await write(newLines);
          await reload();
          setSyncedToFile(true);
        },
      },
      async () => {
        await write(
          state.orderLines.map((ol) => {
            const newOl = new OrderLineModel(ol.toExcelRow(), ol.originalIndex);
            if (deliveryIds.has(newOl.deliveryId)) {
              newOl.sentBusinessCentral = true;
              // only update sentTime if it is not already set or if the order has an error when synced
              if (!newOl.sentTime || newOl.businessCentralStatusCode == "ERROR") {
                newOl.sentTime = new Date().toISOString();
              }
            }
            return newOl;
          })
        );
        await reload();
        return createOrdersClient.createOrders(
          toSend.map((ol) => ({
            ProjectNo: ol.projectNo.toString(),
            DeliveryID: +ol.deliveryId,
            LineNo: +ol.lineNo,
            OrderDescription: ol.orderDescription,
            ItemNo: ol.itemNo,
            ItemDescription: ol.itemDescription,
            ItemQty: +ol.itemQuantity,
            ItemUOM: ol.itemUnit.toString(),
            ItemUnitPrice: ol.itemUnitPrice,
            TransferredDT: new Date().toISOString(),
            TransferredUserID: state.seller || "Selger mangler",
            BizArea: ol.bizArea || undefined,
            Contractstatus: ol.contractStatus?.split(":")[0],
            FerdigMontert: ol.installtionCompleteDate || undefined,
            // ? new Date(ol.installtionCompleteDate).toLocaleDateString("nb-no", {
            //     day: "2-digit",
            //     month: "2-digit",
            //     year: "numeric",
            //   })
            Orderclassification: ol.orderClassification || undefined,
            ProductGroup: ol.productGroup || undefined,
            ProductType: ol.productType || undefined,
          }))
        );
      }
    );
  };

  if (isLoading || (!syncedToFile && !error))
    return <Spinner label="Overfører" labelPosition={"right"} size={SpinnerSize.medium} />;

  return (
    <>
      <Stack>
        <ActionButton
          disabled={!orders.length}
          iconProps={{ iconName: "Send" }}
          text={`Overfør ${orders.length ? orders.length : ""} til BC`}
          onClick={transfer}
        />
      </Stack>
    </>
  );
};
